import {
  CfChangeOrder, CfOrder, CfOrderEvent, CfServiceDetails, CfWholesalerNote, statusLabel,
} from './cfOrders';

/**
 * The Community Fibre API an order went through, as their support name it.
 * An order placed before the record said which went through NPIS, the only
 * surface then.
 */
export const surfaceLabel = (surface?: string): string => {
  if (!surface) return 'NPIS';
  const known: Record<string, string> = { mef: 'MEF', npis: 'NPIS' };
  return known[surface.toLowerCase()] ?? surface;
};

/**
 * How an order was placed, for the list and the card: the Community Fibre
 * API and the products it named (on MEF the Layer 2 OVC and the Layer 3
 * BSM it rides on, on NPIS the OVC alone).
 */
export const placedVia = (o: { order_api?: string; product?: string; access_product?: string }): string => {
  const products = [o.access_product, o.product].filter(Boolean).join(' + ');
  const via = `via ${surfaceLabel(o.order_api)}`;
  return products ? `${via}: ${products}` : via;
};

/** The order id a row shows: Community Fibre's, which is what anyone asks
 * about, or a plain statement that the order never reached them. */
export const rowOrderId = (o: Pick<CfOrder, 'community_fibre_order_id' | 'order_id'>): string => (
  o.community_fibre_order_id || 'Not placed'
);

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** A YYYY-MM-DD day as a person reads it, built from its own parts so no time
 * zone can move it. Empty for anything that is not such a day. */
export const formatDay = (day?: string): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day ?? '');
  if (!m) return '';
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const weekday = new Date(Date.UTC(y, mo - 1, d)).getUTCDay();
  return `${DAYS[weekday]} ${d} ${MONTHS[mo - 1]} ${y}`;
};

/** Unix seconds as a date and time in Melbourne, where the team is. */
export const formatMoment = (seconds?: number): string => {
  if (!seconds) return '';
  return new Date(seconds * 1000).toLocaleString('en-AU', {
    timeZone: 'Australia/Melbourne',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const BY: Record<string, string> = {
  signup: 'by the sign up',
  communityfibre: 'by Community Fibre',
  portal: 'from this portal',
  support: 'by support',
};

/** One line of an order's history. */
export const eventLabel = (e: CfOrderEvent): string => {
  // A note recorded against an order without moving it, such as a CRM
  // failure: the note is the point.
  if (e.from && e.from === e.to && e.detail) {
    return `Note: ${e.detail}`;
  }
  const who = BY[e.trigger] ?? `by ${e.trigger}`;
  const line = `${statusLabel(e.to)} ${who}`;
  return e.detail ? `${line} (${e.detail})` : line;
};

export const customerName = (c?: { first_name?: string; last_name?: string }): string => (
  [c?.first_name, c?.last_name].filter(Boolean).join(' ')
);

type Cancellable = Pick<CfOrder, 'order_id'> & Partial<Pick<CfOrder,
'community_fibre_order_id' | 'customer' | 'address' | 'status' | 'service_number'>>;

/** A live service is not cancelled, it is disconnected. */
export const isLive = (o: Pick<Cancellable, 'status'>): boolean => o.status === 'active';

/** The cancel dialog's title, naming the order by what is known of it. */
export const cancelTitle = (o: Cancellable): string => {
  if (isLive(o)) {
    if (o.service_number) return `Disconnect service ${o.service_number}?`;
    if (o.address) return `Disconnect the service at ${o.address}?`;
    return 'Disconnect this service?';
  }
  if (o.community_fibre_order_id) return `Cancel order ${o.community_fibre_order_id}?`;
  if (o.address) return `Cancel the order at ${o.address}?`;
  return 'Cancel this order?';
};

/** What cancelling will do, built only from the fields the order has. */
export const cancelPrompt = (o: Cancellable): string => {
  const who = o.customer ? ` for ${o.customer}` : '';
  const where = o.address ? ` at ${o.address}` : '';
  if (isLive(o)) {
    return `This service is live${who}${where}. Community Fibre will be asked to disconnect it; `
      + 'it stays connected until they apply the disconnection.';
  }
  if (!o.community_fibre_order_id) {
    return 'This order never reached Community Fibre, so it is cancelled here straight away.';
  }
  return who || where
    ? `Community Fibre will be asked to drop the order${who}${where}.`
    : 'Community Fibre will be asked to drop this order.';
};

/** One change order against the live service, as a line. */
export const changeLabel = (c: CfChangeOrder): string => {
  const what = c.kind === 'delete' ? 'Disconnection' : `Plan change to ${c.plan_id ?? '?'}`;
  const state = c.state ? ` (${c.state})` : '';
  const line = `${what}: ${c.id}${state}`;
  return c.reason ? `${line}. ${c.reason}` : line;
};

/** The order that last changed first: that is the one someone is chasing. */
type Dated = { updated_at: number; created_at: number };

export const newestFirst = <T extends Dated>(rows: T[]): T[] => (
  [...rows].sort((a, b) => (b.updated_at - a.updated_at) || (b.created_at - a.created_at))
);

/** The list's opening sort: the most recently placed order first. */
export const defaultCfOrderSort: { field: 'created_at'; sort: 'desc' }[] = [
  { field: 'created_at', sort: 'desc' },
];

/** The interval the qualification quoted, as the SQ shows it. */
export const leadTime = (days?: number): string => {
  if (!days) return '';
  return days === 1 ? '1 day' : `${days} days`;
};

/**
 * The service as built, laid out for support in the order they ask: the
 * OVC, the port in the apartment and its VLAN, then our side. Empty until
 * Community Fibre have built it.
 */
export const serviceFields = (service?: CfServiceDetails): { label: string; value: string }[] => {
  if (!service) return [];
  const fields: { label: string; value?: string }[] = [
    { label: 'Service (OVC)', value: service.id },
    { label: 'UNI port', value: service.uni },
    { label: 'UNI VLAN (C-tag)', value: service.uni_vlan ? String(service.uni_vlan) : undefined },
    { label: 'ENNI', value: service.enni },
    { label: 'ENNI VLAN (S-tag)', value: service.enni_vlan ? String(service.enni_vlan) : undefined },
    { label: 'Type', value: service.type },
  ];
  return fields.filter((f): f is { label: string; value: string } => Boolean(f.value));
};

/** One note of theirs: when they wrote it, in Melbourne time, and what. */
export const noteLabel = (note: CfWholesalerNote): string => {
  const when = formatMoment(note.at);
  return when ? `${when}: ${note.text}` : note.text;
};
